import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'hooks/useQuery';
import { EPermission } from 'models/permissions';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { EAddressAssetAmount } from 'models/legalEntity';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { StyledInfoItem } from './styles';

interface ILegalEntityAddressTableCard {
  data: ITableDataSource;
  licenceAuthorityValid?: boolean;
}

const LegalEntityAddressTableCard = ({
  data,
  licenceAuthorityValid = true,
}: ILegalEntityAddressTableCard) => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { t } = useTranslations();

  const firearmAmountDisplayMap = {
    [EAddressAssetAmount.NONE]: undefined,
    [EAddressAssetAmount.SINGLE]: (
      <StyledInfoItem>
        <Icon type={EIconTypes.singleAsset} fontSize={18} />
        <span>{t('general.single.firearm.text')}</span>
      </StyledInfoItem>
    ),
    [EAddressAssetAmount.MULTIPLE]: (
      <StyledInfoItem>
        <Icon type={EIconTypes.multipleAssets} fontSize={18} />
        <span>{t('general.multiple.firearm.text')}</span>
      </StyledInfoItem>
    ),
  };

  const ancillaryAmountDisplayMap = {
    [EAddressAssetAmount.NONE]: undefined,
    [EAddressAssetAmount.SINGLE]: (
      <StyledInfoItem>
        <Icon type={EIconTypes.singleAncillaryAsset} fontSize={18} />
        <span>{t('general.single.ancillary.text')}</span>
      </StyledInfoItem>
    ),
    [EAddressAssetAmount.MULTIPLE]: (
      <StyledInfoItem>
        <Icon type={EIconTypes.multipleAncillaryAssets} fontSize={18} />
        <span>{t('general.multiple.ancillary.text')}</span>
      </StyledInfoItem>
    ),
  };

  return (
    <Grid
      container
      columnSpacing={3}
      sx={{
        backgroundColor: colors.gray100,
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        padding: '20px',
        rowGap: 3,
      }}
    >
      <Grid
        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.info.column')}
        </Typography>
        <Grid sx={{ display: 'flex', gap: 2 }}>
          {firearmAmountDisplayMap[data.firearmAmount as EAddressAssetAmount]}
          {ancillaryAmountDisplayMap[data.ancillaryAmount as EAddressAssetAmount]}
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.addressType.column')}
        </Typography>
        {data.typeUniqueId ? (
          <Chips text={data.typeUniqueId} variant={EChipsVariants.primary} />
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.storageAddress.column')}
        </Typography>
        <Typography variant="body2">
          <Chips
            text={
              data.storage
                ? t('general.confirmation.yes.button')
                : t('general.confirmation.no.button')
            }
            variant={data.storage ? EChipsVariants.success : EChipsVariants.inactive}
          />
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.address.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.line1)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.city.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.city)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.stateProvince.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.regionUniqueId)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.zipPostalCode.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.postalCode)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntityDetails.addressTab.gps.column')}
        </Typography>
        <Typography variant="body2">{`${emptyFieldHolder(data.gps)}`}</Typography>
      </Grid>

      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          pt: 2,
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }} sx={{ mb: { xs: 2, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('legalEntityDetails.addressTab.action.column')}:{' '}
          </Typography>
        </Grid>

        <Grid
          size={{ xs: 12, sm: 9 }}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {licenceAuthorityValid && (
            <Button
              id={`legal_entity_details_address-edit-${data.id}`}
              label={t('legalEntityDetails.addressTab.edit.link')}
              variant={EButtonVariants.outlined}
              onClick={() =>
                navigate(
                  `${ERouteLinks.LegalEntityEditAddress.replace(':id', id as string).replace(
                    ':addressId',
                    data.id as string,
                  )}?type=${type}&expand=address`,
                )
              }
              size="small"
              permission={[EPermission.MANAGE_LE]}
              fullWidth
            />
          )}
          {((data.ancillaryAmount as EAddressAssetAmount) !== EAddressAssetAmount.NONE ||
            (data.firearmAmount as EAddressAssetAmount) !== EAddressAssetAmount.NONE) && (
            <Button
              id={`legal_entity_details_address-show-on-map-${data.id}`}
              label={t('legalEntityDetails.addressTab.showOnMap.link')}
              variant={EButtonVariants.outlined}
              onClick={() =>
                navigate(
                  `${ERouteLinks.ViewLegalEntityAddressMap.replace(
                    ':id',
                    id as string,
                  ).replace(':addressId', data.id as string)}?type=${type}&expand=address`,
                )
              }
              size="small"
              fullWidth
            />
          )}
          {licenceAuthorityValid && (
            <Button
              id={`legal_entity_details_address-delete-${data.id}`}
              label={t('legalEntityDetails.addressTab.delete.link')}
              variant={EButtonVariants.outlined}
              onClick={() =>
                navigate(
                  `${ERouteLinks.LegalEntityDeleteAddress.replace(':id', id as string).replace(
                    ':addressId',
                    data.id as string,
                  )}?type=${type}&expand=address`,
                )
              }
              permission={[EPermission.DELETE_LE]}
              size="small"
              fullWidth
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { LegalEntityAddressTableCard };
